import { useState, type ReactNode } from "react";
import { PButton } from "./PButton";
import { PrimeGlyph } from "./PrimeGlyph";
import type { PrimeGlyphName } from "./prime-glyphs";

// SMALL PRIMENG LOOKALIKES THE PLAYER PROFILE'S SIDEBARS USE, DRAWN AS THE PILLARS THEMES
// DRAW THEM. COLORS THAT DIFFER BY THEME ARE IN `pillars-components.css`.

// `p-tag`: 0.75REM BOLD ON A SOLID FILL, 0.25REM 0.4REM, 4PX RADIUS (10REM `rounded`). THE
// THEME DEFINES ONLY SUCCESS / INFO / WARNING / DANGER; "SECONDARY" AND "CONTRAST" FALL BACK
// TO THE DEFAULT BRAND FILL, EXACTLY AS THEY RENDER IN ANGULAR.
export function PTag({ value, severity, rounded = false, className = "" }: { value: ReactNode; severity?: string; rounded?: boolean; className?: string }) {
  const known = severity === "success" || severity === "info" || severity === "warning" || severity === "danger";
  return (
    <span
      className={`pillars-tag${known ? ` pillars-tag-${severity}` : ""} ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.75rem",
        fontWeight: 700,
        // PRIMENG'S OWN `.p-tag-value { line-height: 1.5 }` (COMPONENT CSS, NOT THE THEME)
        lineHeight: 1.5,
        padding: "0.25rem 0.4rem",
        borderRadius: rounded ? "10rem" : 4,
      }}
    >
      {value}
    </span>
  );
}

// `p-message` (AN INLINE MESSAGE): 0.5REM 0.75REM, 4PX RADIUS, NO BORDER, A 1REM ICON 0.5REM
// BEFORE THE TEXT; THE SAME PASTELS IN BOTH THEMES
const MESSAGE_LOOK: Record<"info" | "warn" | "success" | "error", { bg: string; text: string; icon: PrimeGlyphName }> = {
  info: { bg: "#cce5ff", text: "#004085", icon: "info-circle" },
  warn: { bg: "#fff3cd", text: "#856404", icon: "exclamation-triangle" },
  success: { bg: "#d4edda", text: "#155724", icon: "check" },
  error: { bg: "#f8d7da", text: "#721c24", icon: "times-circle" },
};

export function PMessage({ severity, text, className = "" }: { severity: keyof typeof MESSAGE_LOOK; text: string; className?: string }) {
  const look = MESSAGE_LOOK[severity];
  return (
    <div
      role={severity === "error" ? "alert" : "status"}
      className={className}
      style={{ display: "flex", alignItems: "center", padding: "0.5rem 0.75rem", borderRadius: 4, background: look.bg, color: look.text }}
    >
      <span style={{ display: "inline-flex", marginRight: "0.5rem", flexShrink: 0 }}>
        <PrimeGlyph name={look.icon} size="1rem" />
      </span>
      <span style={{ fontSize: "1rem" }}>{text}</span>
    </div>
  );
}

// `p-inputNumber` WITH `[showButtons]`: THE FIELD WITH A STACKED UP / DOWN PAIR OF PRIMARY
// BUTTONS ON ITS RIGHT, CLAMPED TO `min`..`max`
export function PInputNumber({ value, onChange, min, max, ariaLabel }: { value: number; onChange: (v: number) => void; min: number; max: number; ariaLabel: string }) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="p-inputgroup flex w-full" style={{ alignItems: "stretch" }}>
      <input
        className="p-inputtext"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={String(value)}
        onChange={(e) => {
          const n = Number(e.target.value.replace(/\D/g, ""));
          if (Number.isFinite(n) && e.target.value !== "") onChange(clamp(n));
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", width: "2.357rem" }}>
        <PButton icon="chevron-up" ariaLabel={`Increase ${ariaLabel}`} onClick={() => onChange(clamp(value + 1))} sx={{ flex: 1, width: "100%", padding: 0, borderRadius: "0 4px 0 0 !important" }} />
        <PButton icon="chevron-down" ariaLabel={`Decrease ${ariaLabel}`} onClick={() => onChange(clamp(value - 1))} sx={{ flex: 1, width: "100%", padding: 0, borderRadius: "0 0 4px 0 !important" }} />
      </div>
    </div>
  );
}

// `p-checkbox`: A 20PX BOX, 2PX `#ced4da` BORDER, 4PX RADIUS; CHECKED IS `#007bff` WITH A
// WHITE CHECK
export function PCheckbox({ checked, onChange, ariaLabel, id }: { checked: boolean; onChange: (v: boolean) => void; ariaLabel?: string; id?: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      id={id}
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`pillars-checkbox${checked ? " pillars-checkbox-checked" : ""}`}
      style={{ width: 20, height: 20, borderRadius: 4, display: "inline-grid", placeItems: "center", padding: 0, cursor: "pointer", flexShrink: 0 }}
    >
      {checked && <PrimeGlyph name="check" size="0.75rem" />}
    </button>
  );
}

// ONE `p-accordion` TAB, OPEN TO START (`[activeIndex]="0"`): A TOGGLE CHEVRON, THEN THE HEADER
export function PAccordion({ header, children, className = "", onCollapse }: { header: ReactNode; children: ReactNode; className?: string; onCollapse?: () => void }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={className}>
      <button
        type="button"
        className="pillars-accordion-header"
        aria-expanded={open}
        onClick={() => {
          if (open) onCollapse?.();
          setOpen(!open);
        }}
      >
        <span style={{ display: "flex", marginRight: "0.5rem" }}>
          <PrimeGlyph name={open ? "chevron-down" : "chevron-right"} />
        </span>
        {header}
      </button>
      {open && <div className="pillars-accordion-content">{children}</div>}
    </div>
  );
}

// THE ACCORDION HEADER EVERY PROFILE SIDEBAR USES: A BOLD LABEL AND A COUNT BADGE AT THE RIGHT
export function CountHeader({ label, count }: { label: string; count: ReactNode }) {
  return (
    <span className="flex align-items-center gap-2 w-full">
      <span className="font-bold white-space-nowrap"> {label} </span>
      <span className="pillars-badge ml-auto mr-2">{count}</span>
    </span>
  );
}

// `paged-list-view`: A CENTERED "PAGE N OF M (X ITEMS)" HEADER WITH ROUND TEXT CHEVRONS, THEN
// `p-2 pb-0` ROWS RULED BETWEEN, OR THE EMPTY MESSAGE (A SPINNER WHILE THE FIRST PAGE LOADS)
export function PPagedList<T>(props: {
  page: number;
  totalPages: number;
  totalRows: number;
  rows: T[] | undefined;
  loading: boolean;
  error?: boolean;
  emptyMessage: string;
  onPage: (page: number) => void;
  rowKey: (row: T, i: number) => string | number;
  render: (row: T) => ReactNode;
}) {
  const { page, totalPages, totalRows, rows, loading, error, emptyMessage, onPage, rowKey, render } = props;
  const n = (v: number) => v.toLocaleString("en-US");
  return (
    <div>
      <div className="pillars-dataview-header">
        <div className="grid grid-nogutter">
          <div className="col-12">
            <div className="flex flex-row align-items-center justify-content-center font-bold text-sm md:text-lg">
              <PButton icon="chevron-left" rounded variant="text" ariaLabel="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} />
              <div>
                Page {n(page)} of {n(totalPages)} <span className="font-bold">({n(totalRows)} items)</span>
              </div>
              <PButton icon="chevron-right" rounded variant="text" ariaLabel="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)} />
            </div>
          </div>
        </div>
      </div>
      {loading && !rows ? (
        <div className="flex align-items-center justify-content-center p-4">
          <span className="pi-spin text-gray-300" style={{ display: "inline-flex" }}>
            <PrimeGlyph name="spinner" size="3rem" />
          </span>
        </div>
      ) : error && !rows ? (
        <div className="pillars-dataview-empty">Could not be loaded. Please try again.</div>
      ) : !rows || rows.length === 0 ? (
        <div className="pillars-dataview-empty">{emptyMessage}</div>
      ) : (
        <div className="grid grid-nogutter">
          {rows.map((r, i) => (
            <div key={rowKey(r, i)} className="col-12">
              <div className={`p-2 pb-0${i > 0 ? " border-top-1 surface-border" : ""}`}>{render(r)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
